import React, { useState, useEffect } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../hooks/useAuth';
import SocialAuthCompletion from './SocialAuthCompletion';
import './SocialAuthButtons.css';

// A social provider is only offered when its build-time credential is present.
// Without this guard a missing REACT_APP_GOOGLE_CLIENT_ID (e.g. an unset CI
// secret) makes the Google SDK throw "Missing required parameter client_id",
// which bubbles up to the router and replaces the whole page with an error.
// These are inlined at build time, so an unset credential also lets the
// bundler drop the corresponding button entirely.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;

/* Whether this build offers social sign-in at all. Exported because the form
   around these buttons draws an "OR" divider between them and the email
   fields: with no provider configured this component renders nothing, and the
   divider was left stranded above the form separating it from nothing. */
export const socialAuthAvailable = Boolean(GOOGLE_CLIENT_ID || FACEBOOK_APP_ID);

/**
 * The Google button lives in its own component so that useGoogleLogin is only
 * ever mounted when a client ID exists.
 *
 * The hook calls google.accounts.oauth2.initTokenClient from an effect as soon
 * as it mounts — not on click — so with an empty client_id it throws during
 * commit, which React escalates to the router's error boundary ("Unexpected
 * Application Error"). Guarding the button's JSX alone was not enough because
 * the hook still ran; the hook has to be inside the part that isn't rendered.
 */
const GoogleAuthButton = ({ loading, setLoading, setError, onToken }) => {
  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => onToken(tokenResponse.access_token),
    onError: () => {
      setError('Google sign-in failed. Please try again.');
      setLoading(null);
    },
    onNonOAuthError: () => {
      // Handles popup closed by user or blocked by browser
      setLoading(null);
    },
  });

  const handleClick = () => {
    setLoading('google');
    setError('');
    try {
      googleLogin();
    } catch (err) {
      // Never let an SDK/config failure escape into the router's error boundary.
      console.error('Google sign-in could not start:', err);
      setError('Google sign-in is unavailable right now. Please use email and password.');
      setLoading(null);
    }
  };

  return (
    <Button
      variant="outline-primary"
      className="social-btn google-btn"
      onClick={handleClick}
      disabled={loading !== null}
    >
      {loading === 'google' ? (
        <Spinner animation="border" size="sm" className="me-2" />
      ) : (
        <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
          <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
            <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
            <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
            <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
            <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
          </g>
        </svg>
      )}
      Continue with Google
    </Button>
  );
};

const SocialAuthButtons = ({ onSuccess }) => {
  const { socialAuth } = useAuth();

  const [loading, setLoading] = useState(null); // 'google' | 'facebook' | null
  const [error, setError] = useState('');

  // Pending state for new users who need to complete their profile
  const [pendingProvider, setPendingProvider] = useState(null);
  const [pendingToken, setPendingToken] = useState(null);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [showCompletion, setShowCompletion] = useState(false);

  // Load Facebook JS SDK on mount
  useEffect(() => {
    if (!FACEBOOK_APP_ID || window.FB) return;

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
    };

    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const handleSocialResult = async (provider, token) => {
    setLoading(provider);
    setError('');

    try {
      const result = await socialAuth(provider, token);

      if (result.isNewUser) {
        setPendingProvider(provider);
        setPendingToken(token);
        setPendingProfile(result.providerProfile);
        setShowCompletion(true);
      } else if (result.success) {
        onSuccess();
      } else {
        setError(result.error || `${provider} sign-in failed. Please try again.`);
      }
    } catch (err) {
      setError(err.message || `${provider} sign-in failed. Please try again.`);
    } finally {
      setLoading(null);
    }
  };

  const handleFacebookClick = () => {
    if (!window.FB) {
      setError('Facebook is still loading. Please try again in a moment.');
      return;
    }

    setLoading('facebook');
    setError('');

    window.FB.login(
      (response) => {
        if (response.authResponse) {
          handleSocialResult('facebook', response.authResponse.accessToken);
        } else {
          setError('Facebook sign-in was cancelled or failed.');
          setLoading(null);
        }
      },
      { scope: 'email' }
    );
  };

  // Nothing to show when no provider is configured — email/password sign-in
  // in the parent form still works.
  if (!GOOGLE_CLIENT_ID && !FACEBOOK_APP_ID) return null;

  return (
    <>
      <div className="social-auth-buttons">
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}

        {GOOGLE_CLIENT_ID && (
          <GoogleAuthButton
            loading={loading}
            setLoading={setLoading}
            setError={setError}
            onToken={(token) => handleSocialResult('google', token)}
          />
        )}

        {FACEBOOK_APP_ID && (
        <Button
          variant="outline-primary"
          className="social-btn facebook-btn"
          onClick={handleFacebookClick}
          disabled={loading !== null}
        >
          {loading === 'facebook' ? (
            <Spinner animation="border" size="sm" className="me-2" />
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
              <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
          )}
          Continue with Facebook
        </Button>
        )}

        <p className="social-auth-note">
          <small className="text-muted">
            By continuing with social login, you agree to our Terms of Service and Privacy Policy.
          </small>
        </p>
      </div>

      {showCompletion && (
        <SocialAuthCompletion
          show={showCompletion}
          onSuccess={() => {
            setShowCompletion(false);
            onSuccess();
          }}
          onHide={() => {
            setShowCompletion(false);
            setPendingProvider(null);
            setPendingToken(null);
            setPendingProfile(null);
          }}
          provider={pendingProvider}
          token={pendingToken}
          providerProfile={pendingProfile}
        />
      )}
    </>
  );
};

export default SocialAuthButtons;
